import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import {
  getPublishedStatus,
  getWorkingProjectSessions,
  getWorkingProjectUuid,
  selectSingleSessionUuid,
} from '@/store/slices/project/selectors';
import { getBaseAdminURL } from '@/utils/urls';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';
import { EProjectStatus } from '@/types/projects.types';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { TooltipPortalContentDirection, TooltipPortalPosition } from '@ui/tooltip-portal/tooltip-portal-types';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

interface ModerationButtonProps {
  isGradient?: boolean;
  sessionUuid?: string;
}

const ModerationButton = ({ isGradient = false, sessionUuid: sessionUuidOverride }: ModerationButtonProps) => {
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const publishedStatus = useAppSelector(getPublishedStatus);
  const singleSessionUuid = useAppSelector(selectSingleSessionUuid);
  const sessions = useAppSelector(getWorkingProjectSessions);

  const params = useParams();
  const paramsLanguage = params?.language || 'en';

  const sessionUuid = useMemo(() => {
    if (sessionUuidOverride) return sessionUuidOverride;
    if (params?.sessionUuid) return params.sessionUuid;
    if (singleSessionUuid) {
      return singleSessionUuid;
    }
  }, [params?.sessionUuid, sessionUuidOverride, singleSessionUuid]);

  const isPublished = publishedStatus?.status === EProjectStatus.PUBLIC;

  const session = useMemo(() => {
    return (sessions || []).find((s) => s.uuid === sessionUuid);
  }, [sessionUuid, sessions]);

  const handleNavigateToModerator = () => {
    if (!projectUuid || !isPublished || !session) return;
    const language = session?.default_language || paramsLanguage;
    const url = `${getBaseAdminURL()}/moderator/event/${projectUuid}/${language}/session/${session.uuid}`;
    window.open(url, '_self');
  };

  return (
    <OptionalComponent display={!!sessionUuid}>
      <TooltipPortal
        tooltipContent={
          <Text color="var(--gray-true-dark-100)" className="C2">
            Project not yet published
          </Text>
        }
        disabled={isPublished}
        showOnFocus
        keepOpenOnContentHover={false}
        contentDirection={TooltipPortalContentDirection.LTR}
        position={TooltipPortalPosition.RIGHT}
      >
        {isGradient ? (
          <Button
            backgroundColor={getThemeColorCSSVariable()}
            className="radius-100 text-left padding-v-10 padding-h-16 width-100-percent"
            disabled={!isPublished}
            disabledOpacity={0.6}
            onClick={handleNavigateToModerator}
          >
            <Text
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              className="P2"
            >
              Moderate
            </Text>
          </Button>
        ) : (
          <Button
            hoverBackgroundColor="var(--gray-600)"
            outlineOnFocus={false}
            className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent"
            disabled={!isPublished}
            onClick={handleNavigateToModerator}
          >
            <Text color="var(--gray-true-dark-100)" className="P2">
              Moderate
            </Text>
          </Button>
        )}
      </TooltipPortal>
    </OptionalComponent>
  );
};

export default ModerationButton;
